import { useMemo, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AnalyticsFilterBar } from '@/components/analytics/AnalyticsFilterBar';
import { WidgetCard } from '@/components/analytics/WidgetCard';
import { KpiWithDelta } from '@/components/analytics/KpiWithDelta';
import { ExportPdfTrigger } from '@/components/analytics/ExportPdfTrigger';
import { FixtureBadge } from '@/components/analytics/FixtureBadge';
import { Chart } from '@/components/charts/Chart';
import { SectionHeader } from '@/components/analytics/SectionHeader';
import { useActiveUsersData } from '@/hooks/useAnalyticsData';
import { useUIStore } from '@/stores/uiStore';
import { downloadCsv } from '@/utils/csv';
import { exportFilename } from '@/utils/pdf';
import type { SeriesDatum } from '@/components/charts/types';

/**
 * Analytics — Active Users (Remote Access).
 *
 * 14 widgets per `.ai/context/MVP_WIDGET_CATALOG.md` Dashboard 2.
 *
 * Simplifications vs the GoodData reference (documented for the design team
 * + future iterations):
 *   - 2.10 Sessions by Date: rendered as 2-series Line until the dual-axis
 *     Combo chart type lands Week 3+
 *   - 2.11 / 2.12 Most/Least Active Buildings: rendered as paired single-metric
 *     horizontal bars (active users + sessions side by side in the same
 *     widget card) rather than dual-metric bars per row
 *   - 2.13 Sessions by Country: rendered as a placeholder + raw table until
 *     the geo bubble chart lands Week 4
 */
export function ActiveUsers() {
  const { data, loading, error } = useActiveUsersData();
  const session = useUIStore((s) => s.session);

  const uniqueUsersSeries: SeriesDatum[] = useMemo(
    () =>
      (data?.uniqueActiveUsersSeries ?? []).map((p) => ({
        x: p.date,
        Daily: p.dailyUnique,
        '7-Day': p.sevenDayUnique,
        '30-Day': p.thirtyDayUnique,
      })),
    [data]
  );

  const sessionsByDateSeries: SeriesDatum[] = useMemo(
    () =>
      (data?.sessionsByDate ?? []).map((p) => ({
        x: p.date,
        Sessions: p.sessions,
        Hours: p.hours,
      })),
    [data]
  );

  // Grouped-bar shape: one row per node with Sessions + Active Users as
  // separate series rendered side-by-side (matches GoodData widgets 2.11 / 2.12).
  const mostActiveBuildingsSeries: SeriesDatum[] = useMemo(
    () =>
      (data?.mostActiveBuildings ?? []).map((b) => ({
        x: b.nodeName,
        Sessions: b.sessions,
        'Active Users': b.activeUsers,
      })),
    [data]
  );
  const leastActiveBuildingsSeries: SeriesDatum[] = useMemo(
    () =>
      (data?.leastActiveBuildings ?? []).map((b) => ({
        x: b.nodeName,
        Sessions: b.sessions,
        'Active Users': b.activeUsers,
      })),
    [data]
  );

  const pdfTargetRef = useRef<HTMLDivElement>(null);

  const handleExportLeastActiveUsersCsv = () => {
    if (!data) return;
    downloadCsv(
      data.leastActiveUsers.map((u) => ({
        Email: u.email,
        'Last Active At': u.lastActiveAt ?? '(never)',
        Sessions: u.sessions,
      })),
      exportFilename('analytics-least-active-users', 'csv'),
      { report: 'Analytics — Least Active Users' }
    );
  };

  return (
    <div className="dls-space-y-4">
      <div className="dls-flex dls-items-center dls-justify-between">
        <div>
          <h1 className="dls-text-2xl dls-font-bold dls-text-foreground">Active Users</h1>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <Badge variant="outline" className="dls-text-muted-foreground">Remote Access</Badge>
          <FixtureBadge />
          <ExportPdfTrigger
            title={`Active Users${session?.tenant?.name ? ` — ${session.tenant.name}` : ''}`}
            filenameSlug="active-users-remote-access"
            targetRef={pdfTargetRef}
          />
        </div>
      </div>

      <AnalyticsFilterBar />

      {error && (
        <div className="dls-rounded-md dls-border dls-border-status-offline/30 dls-bg-status-offline/10 dls-px-4 dls-py-3 dls-text-sm dls-text-status-offline">
          Failed to load analytics data — {error.message}.
        </div>
      )}

      {/* PDF capture target. */}
      <div ref={pdfTargetRef} className="dls-space-y-4">

      <SectionHeader
        title="Remote Access — Active Users"
        description={
          'Remote Access user activity. "Active users" are those who have logged in and used Remote Access features ' +
          'during the selected window. KPI deltas compare against the same window last year. The 25 Most Active and ' +
          '25 Least Active Buildings widgets rank nodes by session count and show paired metrics for Active Users + ' +
          'Sessions side by side. Sessions by Country reflects IP reverse-lookup, which may be inaccurate.'
        }
      />

      {/* 2.1–2.8 KPIs (8 widgets) — 2 rows × 4 columns */}
      <div className="dls-grid dls-grid-cols-2 md:dls-grid-cols-4 dls-gap-3">
        <WidgetCard title="Total Active Users" info="Year-over-year comparison against the same period last year." hideMenu>
          <KpiWithDelta
            value={loading ? '—' : data?.totalActiveUsers.current ?? 0}
            previousValue={loading ? undefined : data?.totalActiveUsers.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
        <WidgetCard title="Active vs Enabled" info="Active Users as a fraction of total Enabled Users." hideMenu>
          <KpiWithDelta
            value={loading ? '—' : data?.totalActiveUsers.current ?? 0}
            subtitle={loading ? undefined : `of ${data?.totalEnabledUsers ?? 0} Enabled Users`}
          />
        </WidgetCard>
        <WidgetCard title="Avg Users / Node" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : (data?.averageActiveUsersPerNode ?? 0).toFixed(2)}
            subtitle="Active Users per Node"
          />
        </WidgetCard>
        <WidgetCard title="Total Sessions" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : data?.totalSessions.current ?? 0}
            previousValue={loading ? undefined : data?.totalSessions.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
        <WidgetCard title="Avg Session (min)" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : (data?.averageSessionLengthMinutes.current ?? 0).toFixed(2)}
            previousValue={loading ? undefined : data?.averageSessionLengthMinutes.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
        <WidgetCard title="Avg Sessions / User" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : (data?.averageSessionsPerUser.current ?? 0).toFixed(2)}
            previousValue={loading ? undefined : data?.averageSessionsPerUser.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
        <WidgetCard title="Hours of Remote Access" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : (data?.totalRemoteAccessHours.current ?? 0).toLocaleString()}
            previousValue={loading ? undefined : data?.totalRemoteAccessHours.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
        <WidgetCard title="Avg Hours / User" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : (data?.averageHoursPerUser.current ?? 0).toFixed(2)}
            previousValue={loading ? undefined : data?.averageHoursPerUser.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
      </div>

      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-2 dls-gap-4">
        {/* 2.9 Unique Active Users — Multi-line */}
        <WidgetCard
          title="Unique Active Users"
          info="Three rolling windows: Daily / 7-Day / 30-Day unique active users."
        >
          {loading ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">Loading…</div>
          ) : (
            <Chart
              type="line"
              data={uniqueUsersSeries}
              xKey="x"
              seriesKeys={['Daily', '7-Day', '30-Day']}
              height={280}
              ariaLabel="Unique active users — daily, 7-day, 30-day"
            />
          )}
        </WidgetCard>

        {/* 2.10 Sessions by Date — dual-axis combo (sessions count as bars,
            hours as a line on the secondary axis). */}
        <WidgetCard
          title="Sessions by Date"
          info="Daily session count (bars) overlaid with hours of usage (line) on a secondary y-axis."
        >
          {loading ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-[280px] dls-text-sm dls-text-muted-foreground">Loading…</div>
          ) : (
            <Chart
              type="combo"
              data={sessionsByDateSeries}
              xKey="x"
              barKey="Sessions"
              lineKey="Hours"
              barLabel="Sessions"
              lineLabel="Hours of usage"
              dualAxis
              height={280}
              ariaLabel="Sessions by date with hours overlay"
            />
          )}
        </WidgetCard>
      </div>

      {/* 2.11 25 Most Active Buildings — dual-metric grouped bar
          (Sessions + Active Users side-by-side per node, matching GoodData). */}
      <WidgetCard
        title="25 Most Active Buildings"
        info="Top 25 buildings ranked by session count, with Active Users alongside per node. Two bars per node — Sessions and Active Users — matches the GoodData reference layout."
      >
        {loading ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-h-[500px] dls-text-sm dls-text-muted-foreground">Loading…</div>
        ) : (
          <Chart
            type="grouped-bar"
            data={mostActiveBuildingsSeries}
            xKey="x"
            seriesKeys={['Sessions', 'Active Users']}
            orientation="horizontal"
            height={500}
            ariaLabel="Most active buildings — sessions + active users grouped per node"
          />
        )}
      </WidgetCard>

      {/* 2.12 25 Least Active Buildings — same dual-metric pattern. */}
      <WidgetCard
        title="25 Least Active Buildings"
        info="Bottom 25 buildings by session count, with Active Users alongside per node. Cloud/VDI bastions typically appear here."
      >
        {loading ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-h-[500px] dls-text-sm dls-text-muted-foreground">Loading…</div>
        ) : (
          <Chart
            type="grouped-bar"
            data={leastActiveBuildingsSeries}
            xKey="x"
            seriesKeys={['Sessions', 'Active Users']}
            orientation="horizontal"
            height={500}
            ariaLabel="Least active buildings — sessions + active users grouped per node"
          />
        )}
      </WidgetCard>

      {/* 2.13 Sessions by Country — interactive geo bubble map (Visx-only). */}
      <WidgetCard
        title="Sessions by Country"
        info="Geographic distribution of session origins (based on IP reverse-lookup; may be inaccurate). Bubble size = sessions; color intensity = active users. Hover for exact counts. Geo rendering is Visx-only; the Dev Console slicer is bypassed for this widget."
      >
        {loading || !data ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-h-[420px] dls-text-sm dls-text-muted-foreground">Loading…</div>
        ) : data.sessionsByCountry.length === 0 ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-h-[420px] dls-text-sm dls-text-muted-foreground">No data for your filter selection.</div>
        ) : (
          <>
            <Chart
              type="geo"
              data={data.sessionsByCountry.map((c) => ({
                id: c.countryCode,
                label: c.countryName,
                latitude: c.latitude,
                longitude: c.longitude,
                value: c.sessions,
                activeUsers: c.activeUsers,
              }))}
              height={420}
              ariaLabel="Sessions by country — bubble map"
            />
            <p className="dls-text-xs dls-text-muted-foreground dls-italic dls-mt-2 dls-px-1">
              Geo chart renders via Visx regardless of the active chart-library
              selection — Recharts, D3, and ECharts geo implementations are not
              part of MVP scope.
            </p>
          </>
        )}
      </WidgetCard>

      {/* 2.14 Least Active Users */}
      <WidgetCard
        title="Least Active Users"
        info="Users with the lowest session count over the filter window. Sort by last-active to find dormant accounts."
        onExportCsv={data ? handleExportLeastActiveUsersCsv : undefined}
      >
        {loading || !data ? (
          <div className="dls-py-8 dls-text-center dls-text-sm dls-text-muted-foreground">Loading…</div>
        ) : data.leastActiveUsers.length === 0 ? (
          <div className="dls-py-8 dls-text-center dls-text-sm dls-text-muted-foreground">No data for your filter selection.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="dls-text-right">Sessions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.leastActiveUsers.map((u) => (
                <TableRow key={u.email}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell className="dls-font-mono dls-text-xs">{u.lastActiveAt ?? '(never)'}</TableCell>
                  <TableCell className="dls-text-right dls-font-mono">{u.sessions}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </WidgetCard>
      </div>
    </div>
  );
}
